import React, { useEffect, useState, useRef } from 'react';
import api from '../services/api';
import { F1_DRIVERS, driverImgUrl } from '../constants/f1Drivers';
import { Link } from 'react-router-dom';

const SEASON = 2026;

const TEAM_COLORS = {
  'Red Bull Racing': '#3671C6', 'Ferrari': '#E8002D', 'Mercedes': '#27F4D2',
  'McLaren': '#FF8000', 'Aston Martin': '#229971', 'Alpine': '#FF87BC',
  'Williams': '#64C4FF', 'Racing Bulls': '#6692FF', 'Audi': '#52E252',
  'Haas': '#B6BABD', 'Haas F1 Team': '#B6BABD', 'Cadillac': '#CC0000',
};
const teamColor = (t) => TEAM_COLORS[t] || '#71717a';
const posColor = (i) => i === 0 ? '#FFD600' : i === 1 ? '#A8A8B3' : i === 2 ? '#CD7F32' : '#52525b';

const driverImgByNumber = (number) => {
  const d = F1_DRIVERS.find(d => String(d.number) === String(number));
  return d ? driverImgUrl(d.img) : null;
};

// Animated points bar
const PointsBar = ({ points, maxPoints, color, delay = 0 }) => {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setWidth(maxPoints > 0 ? (points / maxPoints) * 100 : 0), delay + 100);
    return () => clearTimeout(t);
  }, [points, maxPoints, delay]);
  return (
    <div className="h-1 rounded-full bg-zinc-800 overflow-hidden mt-1.5" style={{ minWidth: 60, maxWidth: 120 }}>
      <div className="h-full rounded-full transition-all duration-700 ease-out"
        style={{ width: `${width}%`, background: `linear-gradient(90deg, ${color}88, ${color})` }} />
    </div>
  );
};

// Driver row
const DriverRow = ({ d, i, maxPoints, total }) => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const ref = useRef(null);
  const color = teamColor(d.team);
  const imgUrl = driverImgByNumber(d.number);
  const gap = i === 0 ? null : d.points - total[0].points;

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className="transition-all duration-500"
      style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateX(0)' : 'translateX(-16px)', transitionDelay: `${i * 40}ms` }}>
      <div className="card p-0 overflow-hidden hover:border-zinc-600 transition-all group cursor-pointer"
        style={{ borderColor: expanded ? color + '55' : undefined }}
        onClick={() => setExpanded(e => !e)}>

        {/* Hover color wash */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
          style={{ background: `linear-gradient(90deg, ${color}0a 0%, transparent 60%)` }} />

        {/* Main row */}
        <div className="flex items-center gap-3 px-4 py-3 relative">
          {/* Left accent bar */}
          <div className="absolute left-0 top-0 bottom-0 w-0.5 rounded-l transition-all"
            style={{ background: color, opacity: expanded ? 1 : 0 }} />

          {/* Position */}
          <div className="w-7 shrink-0 text-center">
            <span className="text-base font-black" style={{ color: posColor(i) }}>{i + 1}</span>
          </div>

          {/* Avatar */}
          <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0"
            style={{ background: color + '20', border: `1.5px solid ${color}44` }}>
            {imgUrl && <img src={imgUrl} alt={d.name} className="w-full h-full object-cover object-top"
              onError={e => e.target.style.display = 'none'} />}
          </div>

          {/* Name + bar */}
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold text-sm leading-tight truncate">{d.name}</p>
            <p className="text-[11px] font-medium truncate" style={{ color }}>{d.team}</p>
            <PointsBar points={d.points} maxPoints={maxPoints} color={color} delay={i * 40} />
          </div>

          {/* Right stats */}
          <div className="flex items-center gap-3 shrink-0">
            {gap !== null && (
              <span className="text-[11px] text-zinc-500 hidden sm:block font-mono">{gap} pts</span>
            )}
            {d.wins > 0 && (
              <div className="text-center hidden sm:block">
                <p className="text-yellow-400 font-black text-sm leading-tight">{d.wins}</p>
                <p className="text-[9px] text-zinc-600 uppercase tracking-wider">V</p>
              </div>
            )}
            <div className="text-center min-w-[44px]">
              <p className="text-white font-black text-lg leading-tight">{d.points}</p>
              <p className="text-[9px] text-zinc-600 uppercase tracking-wider">pts</p>
            </div>
            <svg className={`w-3.5 h-3.5 text-zinc-600 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="m6 9 6 6 6-6"/>
            </svg>
          </div>
        </div>

        {/* Expanded panel */}
        {expanded && (
          <div className="px-4 pb-3 pt-1 border-t border-zinc-800/60 flex items-center gap-4"
            style={{ background: color + '08' }}>
            <Link to={`/driver/${d.number}`}
              onClick={e => e.stopPropagation()}
              className="text-xs font-bold px-3 py-1.5 rounded-lg transition-colors"
              style={{ background: color + '20', color, border: `1px solid ${color}44` }}>
              Voir le profil →
            </Link>
            <div className="flex gap-4 text-center">
              <div>
                <p className="text-white font-black text-sm">{d.points}</p>
                <p className="text-[10px] text-zinc-500">Points</p>
              </div>
              {d.wins > 0 && <div>
                <p className="text-yellow-400 font-black text-sm">{d.wins}</p>
                <p className="text-[10px] text-zinc-500">Victoire{d.wins > 1 ? 's' : ''}</p>
              </div>}
              {gap !== null && <div>
                <p className="text-zinc-300 font-black text-sm">{gap}</p>
                <p className="text-[10px] text-zinc-500">/ leader</p>
              </div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Constructor row
const ConstructorRow = ({ c, i, maxPoints, driversOfTeam }) => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const ref = useRef(null);
  const color = teamColor(c.name);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className="transition-all duration-500"
      style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateX(0)' : 'translateX(-16px)', transitionDelay: `${i * 50}ms` }}>
      <div className="card p-0 overflow-hidden hover:border-zinc-600 transition-all cursor-pointer"
        style={{ borderColor: expanded ? color + '55' : undefined }}
        onClick={() => setExpanded(e => !e)}>
        {/* Left color band */}
        <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l" style={{ background: color }} />

        {/* Main row */}
        <div className="flex items-center gap-3 pl-5 pr-4 py-3">
          <span className="text-base font-black w-6 shrink-0 text-center" style={{ color: posColor(i) }}>{i + 1}</span>
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold text-sm leading-tight">{c.name}</p>
            <PointsBar points={c.points} maxPoints={maxPoints} color={color} delay={i * 50} />
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {c.wins > 0 && (
              <div className="text-center hidden sm:block">
                <p className="text-yellow-400 font-black text-sm leading-tight">{c.wins}</p>
                <p className="text-[9px] text-zinc-600 uppercase tracking-wider">V</p>
              </div>
            )}
            <div className="text-center min-w-[44px]">
              <p className="text-white font-black text-lg leading-tight">{c.points}</p>
              <p className="text-[9px] text-zinc-600 uppercase tracking-wider">pts</p>
            </div>
            <svg className={`w-3.5 h-3.5 text-zinc-600 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="m6 9 6 6 6-6"/>
            </svg>
          </div>
        </div>

        {/* Expanded: show both drivers */}
        {expanded && driversOfTeam.length > 0 && (
          <div className="border-t border-zinc-800/60 divide-y divide-zinc-800/40"
            style={{ background: color + '06' }}>
            {driversOfTeam.map(d => {
              const imgUrl = driverImgByNumber(d.number);
              return (
                <Link key={d.number} to={`/driver/${d.number}`}
                  onClick={e => e.stopPropagation()}
                  className="flex items-center gap-3 px-5 py-2.5 hover:bg-white/5 transition-colors">
                  <div className="w-7 h-7 rounded-lg overflow-hidden shrink-0"
                    style={{ background: color + '20', border: `1px solid ${color}44` }}>
                    {imgUrl && <img src={imgUrl} alt={d.name} className="w-full h-full object-cover object-top"
                      onError={e => e.target.style.display = 'none'} />}
                  </div>
                  <span className="text-sm text-zinc-300 font-medium flex-1 truncate">{d.name}</span>
                  {d.wins > 0 && <span className="text-xs text-yellow-400 font-bold mr-2">{d.wins}V</span>}
                  <span className="text-sm font-black text-white">{d.points} <span className="text-zinc-600 text-[10px] font-normal">pts</span></span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const Standings = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('drivers');
  const [search, setSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState(null);

  useEffect(() => {
    api.get(`/races/standings?season=${SEASON}`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const teams = data ? [...new Set(data.drivers.map(d => d.team))].sort() : [];
  const filteredDrivers = (data?.drivers || []).filter(d =>
    (!teamFilter || d.team === teamFilter) &&
    (!search || d.name.toLowerCase().includes(search.toLowerCase()))
  );
  const maxDriverPts = data?.drivers?.[0]?.points || 1;
  const maxConstructorPts = data?.constructors?.[0]?.points || 1;

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">

      {/* Header */}
      <div className="mb-6">
        <p className="text-zinc-500 text-xs uppercase tracking-widest font-medium mb-1">Saison {SEASON}</p>
        <h1 className="text-3xl font-black text-white">Classement <span className="text-f1red">F1</span></h1>
        {data && (
          <p className="text-zinc-500 text-sm mt-1">
            {data.racesCompleted} course{data.racesCompleted !== 1 ? 's' : ''} comptabilisée{data.racesCompleted !== 1 ? 's' : ''}
            {data.drivers?.[0] && <span className="ml-2 text-zinc-600">· Leader : <span className="text-white font-bold">{data.drivers[0].name}</span> {data.drivers[0].points} pts</span>}
          </p>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-5">
        {[['drivers', '🏎 Pilotes'], ['constructors', '🏭 Constructeurs']].map(([t, label]) => (
          <button key={t} onClick={() => { setTab(t); setSearch(''); setTeamFilter(null); }}
            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${tab === t ? 'bg-f1red text-white shadow-lg shadow-f1red/20' : 'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <div className="w-8 h-8 border-2 border-f1red border-t-transparent rounded-full animate-spin" />
          <p className="text-zinc-500 text-sm">Chargement du classement…</p>
        </div>
      ) : !data ? (
        <div className="card text-center py-12 text-zinc-500">Aucune donnée pour la saison {SEASON}.</div>
      ) : tab === 'drivers' ? (
        <>
          {/* Search + team filter */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher un pilote…"
                className="w-full pl-9 pr-4 py-2.5 text-sm text-white placeholder-zinc-500 rounded-xl bg-zinc-900 border border-zinc-800 focus:outline-none focus:border-f1red/60 transition-colors" />
              {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white text-lg leading-none">×</button>}
            </div>
          </div>

          {/* Team filter pills */}
          <div className="flex gap-1.5 flex-wrap mb-4">
            <button onClick={() => setTeamFilter(null)}
              className={`px-3 py-1 rounded-full text-[11px] font-bold transition-all border ${!teamFilter ? 'bg-zinc-700 border-zinc-600 text-white' : 'border-zinc-800 text-zinc-500 hover:text-zinc-300 hover:border-zinc-600'}`}>
              Tous
            </button>
            {teams.map(t => (
              <button key={t} onClick={() => setTeamFilter(f => f === t ? null : t)}
                className={`px-3 py-1 rounded-full text-[11px] font-bold transition-all border ${teamFilter === t ? 'text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
                style={teamFilter === t
                  ? { background: teamColor(t) + '30', borderColor: teamColor(t) + '88', color: teamColor(t) }
                  : { borderColor: '#27272a' }}>
                {t}
              </button>
            ))}
          </div>

          {/* Drivers list */}
          <div className="flex flex-col gap-2">
            {filteredDrivers.length === 0
              ? <div className="card text-center py-8 text-zinc-500 text-sm">Aucun pilote trouvé.</div>
              : filteredDrivers.map((d, i) => (
                  <DriverRow key={d.number} d={d} i={data.drivers.indexOf(d)} maxPoints={maxDriverPts} total={data.drivers} />
                ))
            }
          </div>
        </>
      ) : (
        /* Constructors */
        <div className="flex flex-col gap-2">
          {data.constructors.map((c, i) => {
            const driversOfTeam = (data.drivers || []).filter(d => d.team === c.name).sort((a, b) => b.points - a.points);
            return <ConstructorRow key={c.name} c={c} i={i} maxPoints={maxConstructorPts} driversOfTeam={driversOfTeam} />;
          })}
        </div>
      )}
    </div>
  );
};

export default Standings;
